import { useTranslation } from '@gimlite/common';
import { useStoreUIV1, Wizard } from '@gimlite/ui-v1';
import { observer } from 'mobx-react-lite';
import {
  ArticleCode,
  ContractEntity,
  PoolContractEntity,
} from '../../client/graphql';
import { DefaultContractBaseWizard } from '../../common/defaultValue/wizard';
import { CredentialType } from '../../common/mapper/credential.mapper';
import { MasterContractStep } from '../../common/steps/masterContract/masterContract.step';
import { SubContractStep } from '../../common/steps/subContract/subContract.step';
import { portal } from '../../config';
import { ContractBaseFormType } from '../../form/contract-create/contract-create.form';
import { ContractsCreateFormType } from '../../form/contracts-create/contracts-create.form';
import { ConfirmationStep } from './steps/confirmation/confirmation.step';
import { ContractStep } from './steps/contract/contract.step';
import { ParkersStep } from './steps/parkers/parkers.step';
import { SummaryStep } from './steps/summary/summary.step';

export type AddSeveralMotoristsToAnExistingPoolContractWizardContext = {
  parkingId: string | null;
  masterPoolContractId: string | null;
  product: {
    id: string | null;
    credentials: CredentialType.GlobalName[];
    articleCodes: Array<ArticleCode>;
  };
  subPoolContractId: string | null;
  parentContract: ContractBaseFormType.Data.ContractBase | null;
  contractBase: ContractBaseFormType.Data.ContractBase;
  contractsDetails: {
    created: ContractsCreateFormType.Data.Created[];
    checked: ContractEntity[];
  };
  contractMode: 'create' | 'exist';
  contractExistedSave: ContractEntity[];
  subPoolExistedSave: PoolContractEntity[];
  masterPoolExistedSave: PoolContractEntity[];
  mode: typeof portal;
  skipSubContract: (skip: boolean) => void;
};

export const AddSeveralMotoristsToAnExistingPoolContractWizard = observer(
  () => {
    const { t, lang } = useTranslation();
    const { WizardStore } = useStoreUIV1();

    const { parkingId, skipSubContract, mode } = WizardStore?.selected?.store;

    return (
      <Wizard<AddSeveralMotoristsToAnExistingPoolContractWizardContext>
        config={{
          title: t(`addSeveralMotoristsToAnExistingPoolContract`),
        }}
        data={{
          defaultContext: {
            parkingId,
            product: {
              id: null,
              credentials: [],
              articleCodes: [],
            },
            masterPoolContractId: null,
            subPoolContractId: null,
            parentContract: null,
            contractBase: DefaultContractBaseWizard({ t, lang }),
            contractsDetails: {
              checked: [],
              created: [],
            },
            contractMode: 'create',
            contractExistedSave: [],
            subPoolExistedSave: [],
            masterPoolExistedSave: [],
            mode,
            skipSubContract: (skip) => {
              WizardStore.updateStore({
                skipSubContract: skip,
              });
            },
          },
        }}
        handleEvent={{
          done: async (data: any) => {
            console.log('done', { data });
          },
          clear: () => {
            console.log('CLEAR');
          },
          close: () => {
            WizardStore.clear();
          },
        }}
      >
        <Wizard.Item
          config={{ title: t('masterContract'), component: MasterContractStep }}
        />
        {!skipSubContract && (
          <Wizard.Item
            config={{ title: t('subContract'), component: SubContractStep }}
          />
        )}
        <Wizard.Item
          config={{ title: t('contract'), component: ContractStep }}
        />
        <Wizard.Item config={{ title: t('parkers'), component: ParkersStep }} />
        <Wizard.Item
          config={{ title: t('confirmation'), component: ConfirmationStep }}
        />
        <Wizard.Item config={{ title: t('summary'), component: SummaryStep }} />
      </Wizard>
    );
  },
);
